import client from "./client";
import type { HealthEventPage } from "../types/health";
import type { IncidentDetail, IncidentPostmortem, PostmortemImpact } from "../types/incident";

export const statusApi = {
  getSummary: (params?: { platform?: string }) => client.get("/status", { params }),
  listUrls: (params?: { platform?: string }) => client.get("/status/urls", { params }),
  addUrl: (name: string, url: string, alertThreshold: number, notificationChannels: string, platform: string = "phoenix") =>
    client.post("/status/urls", { name, url, alert_threshold: alertThreshold, notification_channels: notificationChannels, platform }),
  updateUrl: (id: string, data: Record<string, unknown>) => client.patch(`/status/urls/${id}`, data),
  deleteUrl: (id: string) => client.delete(`/status/urls/${id}`),
  checkNow: () => client.post("/status/check-now"),
  getAlertRouting: () => client.get("/status/alert-routing"),
  updateAlertRouting: (routing: { critical: string; high: string; medium: string; low: string; slack_channels: string[] }) =>
    client.put("/status/alert-routing", routing),
  getIncidents: (params?: Record<string, unknown>) => client.get("/status/incidents", { params }),
  markAllIncidentsRead: () => client.post("/status/incidents/read-all"),
  getIncident: (id: string) => client.get<IncidentDetail>(`/status/incidents/${id}`),
  getIncidentPostmortem: (id: string) =>
    client.get<IncidentPostmortem>(`/status/incidents/${id}/post-mortem`),
  saveIncidentPostmortem: (
    id: string,
    data: { notes?: string | null; root_cause?: string | null; impact?: PostmortemImpact | null },
  ) => client.patch<IncidentPostmortem>(`/status/incidents/${id}/post-mortem`, data),
  resolveIncident: (id: string) => client.patch(`/status/incidents/${id}/resolve`),
  overrideIncident: (
    id: string,
    data: { severity?: string; status?: string; resolved_at?: string | null; reasoning: string },
  ) => client.patch(`/status/incidents/${id}`, data),
  deleteIncident: (id: string) => client.delete(`/status/incidents/${id}`),
  testAlert: (service_name: string, service_type: string) =>
    client.post("/status/test-alert", { service_name, service_type }),
  getKpis: (params: { days?: number; from_dt?: string; to_dt?: string; platform?: string; source?: string }) =>
    client.get("/status/kpis", { params }),
  getNarrative: (params: { days?: number; from_dt?: string; to_dt?: string; platform?: string }) =>
    client.get("/status/narrative", { params }),
  getUptimeGraph: (params: { days?: number; from_dt?: string; to_dt?: string; platform?: string; source?: string }) =>
    client.get("/status/uptime-graph", { params }),
  getSentryIssues: (params?: { days_back?: number; query?: string; platform?: string }) =>
    client.get("/status/sentry-issues", { params }),
  startSentryAnalysis: (days_back?: number, platform?: string) =>
    client.post("/status/sentry-analyse", null, { params: { days_back, platform } }),
  pollSentryAnalysis: (job_id: string) =>
    client.get(`/status/sentry-analyse/${job_id}`),
  getOverview: (params?: { days?: number }) =>
    client.get("/status/overview", { params }),
  listSentryConfigs: () =>
    client.get("/status/sentry-config"),
  upsertSentryConfig: (platform: string, data: { auth_token?: string | null; org_slug?: string | null; project_slugs?: string | null }) =>
    client.put(`/status/sentry-config/${platform}`, data),
  listLogGroups: (platform: string) =>
    client.get("/status/cloudwatch-log-groups", { params: { platform } }),
  getCloudWatchLogs: (params: { platform: string; log_group: string; hours?: number; filter_pattern?: string; limit?: number }) =>
    client.get("/status/cloudwatch-logs", { params }),
  getHealthEvents: (params?: {
    source?: string;
    category?: string;
    platform?: string;
    from?: string;
    to?: string;
    page?: number;
    page_size?: number;
  }) => client.get<HealthEventPage>("/status/health-events", { params }),
};
